import axios from "axios";
import { API_URL } from "../config";
import { showToast } from "../components/toast";

export const firebaseConfig = {
  apiKey: "AIzaSyD2UytunrmkioEkWXwFRLvsHLyGYlgVk2Q",
  authDomain: "streetgains-c407d.firebaseapp.com",
  databaseURL: "https://streetgains-c407d.firebaseio.com",
  projectId: "streetgains-c407d",
  storageBucket: "streetgains-c407d.firebasestorage.app",
  messagingSenderId: "561631587931",
  appId: "1:561631587931:web:897a45a4f6034f4450d97e",
  measurementId: "G-ZZV6P98FP2"
};

/**
 * Check if the browser supports notifications and service worker
 */
export const isNotificationSupported = () => {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator
  );
};

/**
 * Returns current permission: 'granted' | 'denied' | 'default' | 'unsupported'
 */
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return "unsupported";
  return Notification.permission;
};

/**
 * Ensure Firebase App & Messaging is initialized
 */
const getFirebaseMessaging = async () => {
  if (typeof window === "undefined") return null;

  // Wait briefly for CDN scripts to load if needed
  let retries = 0;
  while (!window.firebase && retries < 10) {
    await new Promise((r) => setTimeout(r, 200));
    retries++;
  }

  if (!window.firebase) {
    console.warn("Firebase SDK script not loaded");
    return null;
  }

  if (!window.firebase.apps || window.firebase.apps.length === 0) {
    window.firebase.initializeApp(firebaseConfig);
  }

  try {
    return window.firebase.messaging();
  } catch (err) {
    console.error("Firebase messaging initialization failed:", err);
    return null;
  }
};

/**
 * Request notification permission, register service worker, generate FCM token and save to backend
 */
export const requestAndSaveFcmToken = async () => {
  if (!isNotificationSupported()) {
    showToast.warning("Push notifications are not supported in this browser.");
    return { success: false, status: "unsupported" };
  }

  try {
    // 1. Request Browser Permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      if (permission === "denied") {
        showToast.error("Notifications were blocked. Please enable them in browser site settings.");
      }
      return { success: false, status: permission };
    }

    // 2. Register Service Worker
    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/"
    });
    await navigator.serviceWorker.ready;

    // 3. Get Firebase Messaging Instance
    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      showToast.error("Could not initialize notification service.");
      return { success: false, status: "firebase_unavailable" };
    }

    // 4. Retrieve FCM Token
    const token = await messaging.getToken({
      serviceWorkerRegistration: registration
    });

    if (!token) {
      showToast.error("Failed to generate device notification token.");
      return { success: false, status: "no_token" };
    }

    console.log("FCM Web Push Token generated:", token);
    localStorage.setItem("fcm_token", token);
    localStorage.setItem("fcmToken", token);

    // 5. Send Token to Backend to store in tbl_userinfo
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      try {
        await axios.post(
          `${API_URL}auth/save-fcm-token`,
          { fcm_token: token },
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json"
            }
          }
        );
        console.log("FCM token saved to tbl_userinfo successfully");
      } catch (apiError) {
        console.warn("Could not save FCM token to backend immediately:", apiError?.message || apiError);
      }
    }

    showToast.success("Notifications enabled successfully!");
    window.dispatchEvent(new CustomEvent("fcmNotificationPermissionChanged", { detail: { permission: "granted", token } }));
    return { success: true, status: "granted", token };
  } catch (error) {
    console.error("Error enabling notifications:", error);
    showToast.error("Error enabling notifications: " + (error?.message || "Unknown error"));
    return { success: false, status: "error", error };
  }
};

/**
 * Setup foreground push notification listener when CRM is open
 */
export const setupForegroundPushListener = (onMessageCallback) => {
  if (!isNotificationSupported()) return () => {};

  getFirebaseMessaging().then((messaging) => {
    if (messaging && typeof messaging.onMessage === "function") {
      messaging.onMessage((payload) => {
        console.log("Foreground push notification received:", payload);
        const title = payload.notification?.title || payload.data?.title || "Streetgains Notification";
        const message = payload.notification?.body || payload.data?.body || "";

        if (onMessageCallback) {
          onMessageCallback(payload);
        } else {
          showToast.info(`${title}: ${message}`);
        }
      });
    }
  });
};
